import {
    Button,
    Field,
    Input,
    makeStyles,
    Menu,
    MenuButtonProps,
    MenuDivider,
    MenuGroup,
    MenuGroupHeader,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Spinner,
    SplitButton,
    Tooltip,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { Cart24Filled, Cart24Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { memo, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAddItemsToCurrentCart, useCreateNewCart, useGetCurrentCartSelector, useSetCurrentCart } from '../../api/cart/cart';
import { ISelectedTaxReturnsAndFilters } from '../../data-types/exportReports';
import { useDefaultModalStyles } from '../../hooks/useDefaultModalStyles';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';
import { useServiceDiscovery } from '../../hooks/useServiceDiscovery';
import { CreateNewCartRequest, RecentCartInfo } from '../../model';
import { validNameRuleRegExp } from '../../utils/RegExpUtils';
import FunctionalAccessDisableWrapper from './FunctionalAccessDisableWrapper';
import Modal from './ModalV9';

export interface ICartButtonProps {
    disabled?: boolean;
    disableMenu?: boolean;
    getReportsToExport: () => ISelectedTaxReturnsAndFilters;
}

const useStyles = makeStyles({
    processingSpinner: {
        paddingLeft: '5px',
        opacity: 0.5,
        fontSize: '10px',
    },
    /** Keep adjoining edges squared so chrome styles do not undo SplitButton join. */
    splitPrimary: {
        borderTopRightRadius: '0',
        borderBottomRightRadius: '0',
    },
    splitMenu: {
        borderTopLeftRadius: '0',
        borderBottomLeftRadius: '0',
    },
    cartNameInput: {
        minWidth: '400px',
    },
    modalBody: {
        paddingTop: '5px',
        paddingRight: '5px',
        paddingBottom: '5px',
        paddingLeft: '5px',
    },
});

const CartButton: React.FC<ICartButtonProps> = memo(({ getReportsToExport, disabled, disableMenu }) => {
    const { t } = useTranslation();
    const [isAddToCartModalOpen, { setFalse: closeAddToCartModalOpen, setTrue: openAddToCartModalOpen }] = useBoolean(false);
    const [cartName, setCartName] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const { cartManagerUrl } = useServiceDiscovery();
    const { functionalAccess } = useFunctionalAccess();

    const queryClient = useQueryClient();
    const { data, queryKey: getCurrentCartSelectorQueryKey } = useGetCurrentCartSelector();

    const { mutateAsync: addItemsToCurrentCart, isLoading: isAddItemsToCurrentCartLoading } = useAddItemsToCurrentCart({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries(getCurrentCartSelectorQueryKey);
            },
        },
    });

    const { mutateAsync: addNewCart, isLoading: isLoadingCreateCart } = useCreateNewCart({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries(getCurrentCartSelectorQueryKey);
            },
        },
    });

    const { mutateAsync: setCurrentCartAsync, isLoading: IsLoadingSetCart } = useSetCurrentCart({
        mutation: {
            onSuccess: (res: any) => {
                if (res.success) queryClient.invalidateQueries(getCurrentCartSelectorQueryKey);
            },
        },
    });

    const currentCart: RecentCartInfo | undefined = data?.currentCart;
    const currentCartNameTooltip = currentCart?.name ? t('currentCart') + currentCart.name : t('noCartSelected');
    const currentCartItemsCount = currentCart ? `(${currentCart.itemsNumber})` : '';

    const isLoading = isLoadingCreateCart || IsLoadingSetCart;

    const clearModalStateAndClose = () => {
        if (!isLoading) {
            setCartName('');
            setErrorMessage(undefined);
        }

        closeAddToCartModalOpen();
    };

    const updateInputTextValue = (_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        const value = data.value;
        if (value !== undefined && validNameRuleRegExp.test(value)) {
            setCartName(value);
            setErrorMessage(undefined);
        }
        if (value !== undefined && value.length >= 60) {
            setErrorMessage(t('cartNameLengthExceeded').toString());
        }
    };

    const createNewCart = async () => {
        if (validNameRuleRegExp.test(cartName) && cartName) {
            const request: CreateNewCartRequest = {
                name: cartName.trim(),
            };
            try {
                const response = await addNewCart({ data: request });
                if (response.errors && response.errors.length > 0) {
                    const errorMessageToDisplay = response.errors[0].message ?? t('addToCartGenericErrorMessage').toString();
                    setErrorMessage(errorMessageToDisplay);
                } else {
                    clearModalStateAndClose();
                }
            } catch {
                setErrorMessage(t('addToCartGenericErrorMessage').toString());
            }
        } else {
            setErrorMessage(t('cartNameIsRequired').toString());
        }
    };

    const styles = useStyles();
    const { button } = useDefaultModalStyles();

    const addToCartModalButtons = (
        <>
            <Button
                appearance='primary'
                size='small'
                aria-label={t('createNewCart').toString()}
                onClick={createNewCart}
                disabled={Boolean(!validNameRuleRegExp.test(cartName) || !cartName || isLoading)}
                className={button}
            >
                {isLoading ? t('working').toString() : t('createNewCart').toString()}
                {isLoading && <Spinner size='extra-tiny' className={styles.processingSpinner} />}
            </Button>
            <Button
                size='small'
                aria-label={t('cancel').toString()}
                onClick={clearModalStateAndClose}
                disabled={isLoading}
                className={button}
            >
                {t('cancel').toString()}
            </Button>
        </>
    );

    const cartMenuList = (
        <MenuList aria-label={t('cart').toString()}>
            <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.cartDropdownEnabled}>
                <MenuItem id='item-add-to-new-cart' icon={<Cart24Regular />} disabled={isLoading} onClick={openAddToCartModalOpen}>
                    {t('addNewCart').toString()}
                </MenuItem>
            </FunctionalAccessDisableWrapper>
            {(data?.recentCarts?.length ?? 0) > 0 && (
                <>
                    <MenuDivider />
                    <MenuGroup>
                        <MenuGroupHeader>{t('recentCarts').toString()}</MenuGroupHeader>
                        {data?.recentCarts?.map((cartInfo) => (
                            <MenuItem key={cartInfo.id} onClick={() => setCurrentCartAsync({ data: { cartId: cartInfo.id } })}>
                                {`${cartInfo.name} (${cartInfo.itemsNumber})`}
                            </MenuItem>
                        ))}
                    </MenuGroup>
                </>
            )}
        </MenuList>
    );

    const addSelectedItemsToCurrentCart = async () => {
        const { selectedTaxReturns } = getReportsToExport();
        const selectedTaxReturnIds = selectedTaxReturns?.map((report) => report.taxReturnItemId!);

        await addItemsToCurrentCart({ data: { cartItemIds: selectedTaxReturnIds } });
    };

    const redirectToPortalCartManager = () => {
        window.open(cartManagerUrl, '_blank');
    };

    const renderContent = (): ReactNode => (
        <div className={styles.modalBody}>
            <Field label={t('newCartName').toString()} validationMessage={errorMessage} validationState={errorMessage ? 'error' : 'none'}>
                <Input
                    autoFocus
                    aria-label={t('newCartName').toString()}
                    placeholder={t('newCart').toString()}
                    value={cartName}
                    className={styles.cartNameInput}
                    onChange={updateInputTextValue}
                    disabled={isLoading}
                />
            </Field>
        </div>
    );

    const buttonAriaLabel = `${currentCartNameTooltip}. ${currentCart?.itemsNumber ?? 0} items`;
    const dataTestId = 'add-new-cart-button-split-button';

    const cartIconButon = (
        <Menu positioning='below-end'>
            <MenuTrigger disableButtonEnhancement>
                {(triggerProps: MenuButtonProps) => (
                    <SplitButton
                        icon={<Cart24Filled />}
                        primaryActionButton={{
                            onClick: redirectToPortalCartManager,
                            'aria-label': buttonAriaLabel,
                            'data-testid': dataTestId,
                            'data-analytics-button-name': 'add_new_cart_button',
                            className: styles.splitPrimary,
                            disabled: isLoading,
                            disabledFocusable: isLoading,
                        }}
                        menuButton={{
                            ...triggerProps,
                            'aria-label': t('cartMenu').toString(),
                            className: mergeClasses(styles.splitMenu, triggerProps.className),
                            disabled: isLoading || Boolean(disableMenu),
                            disabledFocusable: isLoading || Boolean(disableMenu),
                        }}
                    >
                        {isLoading ? t('working').toString() : currentCartItemsCount.trim()}
                        {isLoading && <Spinner size='extra-tiny' className={styles.processingSpinner} />}
                    </SplitButton>
                )}
            </MenuTrigger>
            <MenuPopover>{cartMenuList}</MenuPopover>
        </Menu>
    );

    const canAccessCartButton = functionalAccess?.cartButtonEnabled;

    return (
        <>
            <FunctionalAccessDisableWrapper
                hasFunctionalAccess={functionalAccess?.cartButtonEnabled && functionalAccess?.addToCartButtonEnabled}
            >
                <Button
                    aria-label={t('addToCart').toString()}
                    icon={<Cart24Filled />}
                    onClick={addSelectedItemsToCurrentCart}
                    disabled={Boolean(disabled || isAddItemsToCurrentCartLoading)}
                    data-testid='cart-icon-button'
                >
                    {isAddItemsToCurrentCartLoading ? t('working').toString() : t('addToCart').toString()}
                    {isAddItemsToCurrentCartLoading && <Spinner size='extra-tiny' className={styles.processingSpinner} />}
                </Button>
            </FunctionalAccessDisableWrapper>
            {canAccessCartButton ? (
                <Tooltip content={currentCartNameTooltip} relationship='description' appearance='inverted' withArrow positioning='before'>
                    <span>{cartIconButon}</span>
                </Tooltip>
            ) : (
                <FunctionalAccessDisableWrapper hasFunctionalAccess={canAccessCartButton}>{cartIconButon}</FunctionalAccessDisableWrapper>
            )}
            <Modal
                isOpen={isAddToCartModalOpen}
                onClose={clearModalStateAndClose}
                headerContent={t('addNewCart')}
                bodyContent={renderContent()}
                buttons={addToCartModalButtons}
            />
        </>
    );
});

export default CartButton;
